import { adminSupabase } from './supabaseService';
import { userService } from './userService';

export interface AutoTraderRule {
    id: string;
    user_id: string;
    name: string;
    is_active: boolean;
    token_address: string;
    token_symbol?: string;
    token_name?: string;
    trade_type: 'buy' | 'sell';
    created_at: string;
    updated_at: string;
    last_triggered_at?: string;
    total_triggers: number;
    total_executed_trades: number;
    conditions: AutoTraderCondition[];
    sol_amount_per_trade?: number;
    max_trades_to_execute?: number;
    trading_wallet_addresses?: string[];
    total_sol_budget?: number;
    slippage_percent?: number;
    configuration_metadata?: any;
}

export interface AutoTraderCondition {
    id?: string;
    rule_id?: string;
    field: 'marketCap' | 'liquidity' | 'holders' | 'buys' | 'sells' | 'curvePercentage' | 'txAmount' | 'txVolume' | 'name' | 'symbol';
    operator: '>' | '<' | '>=' | '<=' | '=' | 'increase%' | 'decrease%';
    value: number;
    timeframe?: '1m' | '5m' | '15m' | '1h';
    created_at?: string;
}

export interface AutoTraderTradeHistory {
    id: string;
    rule_id: string | null;
    user_id: string;
    token_address: string;
    token_name?: string;
    token_symbol?: string;
    trading_wallet_address: string;
    amount_sol: number;
    slippage_percent: number;
    transaction_signature?: string;
    status: 'pending' | 'success' | 'failed';
    error_message?: string;
    rule_name?: string;
    trade_type: 'buy' | 'sell';
    triggered_conditions?: any[];
    created_at: string;
    executed_at?: string;
}

export const autoTraderRulesService = {
    /**
     * Get all auto trader rules for a user
     */
    async getUserAutoTraderRules(walletAddress: string): Promise<AutoTraderRule[]> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return [];
            }

            // Get rules with their conditions
            const { data: rules, error } = await adminSupabase
                .from('auto_trader_rules')
                .select(`
                    *,
                    auto_trader_conditions (*)
                `)
                .eq('user_id', user.id)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('Error fetching auto trader rules:', error);
                return [];
            }

            // Transform the data to match our interface
            return (rules || []).map(rule => ({
                ...rule,
                conditions: rule.auto_trader_conditions || []
            }));
        } catch (error) {
            console.error('Error in getUserAutoTraderRules:', error);
            return [];
        }
    },

    /**
     * Create a new auto trader rule
     */
    async createAutoTraderRule(
        walletAddress: string,
        name: string,
        tokenAddress: string,
        tokenSymbol: string,
        tokenName: string,
        tradeType: 'buy' | 'sell',
        conditions: Omit<AutoTraderCondition, 'id' | 'rule_id' | 'created_at'>[],
        isActive: boolean = false,
        config?: {
            solAmountPerTrade?: number;
            maxTradesToExecute?: number;
            slippagePercent?: number;
            tradingWalletAddresses?: string[];
            totalSolBudget?: number;
            configurationMetadata?: any;
        }
    ): Promise<AutoTraderRule | null> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return null;
            }

            // Create the rule
            const { data: rule, error: ruleError } = await adminSupabase
                .from('auto_trader_rules')
                .insert({
                    user_id: user.id,
                    name,
                    token_address: tokenAddress,
                    token_symbol: tokenSymbol,
                    token_name: tokenName,
                    trade_type: tradeType,
                    is_active: isActive,
                    sol_amount_per_trade: config?.solAmountPerTrade,
                    max_trades_to_execute: config?.maxTradesToExecute,
                    slippage_percent: config?.slippagePercent,
                    trading_wallet_addresses: config?.tradingWalletAddresses,
                    total_sol_budget: config?.totalSolBudget,
                    configuration_metadata: config?.configurationMetadata
                })
                .select()
                .single();

            if (ruleError) {
                console.error('Error creating auto trader rule:', ruleError);
                return null;
            }

            // Create the conditions
            if (conditions.length > 0) {
                const conditionsToInsert = conditions.map(condition => ({
                    rule_id: rule.id,
                    field: condition.field,
                    operator: condition.operator,
                    value: condition.value,
                    timeframe: condition.timeframe
                }));

                const { error: conditionsError } = await adminSupabase
                    .from('auto_trader_conditions')
                    .insert(conditionsToInsert);

                if (conditionsError) {
                    console.error('Error creating auto trader conditions:', conditionsError);
                    // Clean up the rule if conditions failed
                    await adminSupabase
                        .from('auto_trader_rules')
                        .delete()
                        .eq('id', rule.id);
                    return null;
                }
            }

            // Return the complete rule with conditions
            return await this.getAutoTraderRuleById(walletAddress, rule.id);
        } catch (error) {
            console.error('Error in createAutoTraderRule:', error);
            return null;
        }
    },

    /**
     * Get a specific auto trader rule by ID
     */
    async getAutoTraderRuleById(walletAddress: string, ruleId: string): Promise<AutoTraderRule | null> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return null;
            }

            const { data: rule, error } = await adminSupabase
                .from('auto_trader_rules')
                .select(`
                    *,
                    auto_trader_conditions (*)
                `)
                .eq('id', ruleId)
                .eq('user_id', user.id)
                .single();

            if (error) {
                console.error('Error fetching auto trader rule:', error);
                return null;
            }

            return {
                ...rule,
                conditions: rule.auto_trader_conditions || []
            };
        } catch (error) {
            console.error('Error in getAutoTraderRuleById:', error);
            return null;
        }
    },

    /**
     * Update an auto trader rule
     */
    async updateAutoTraderRule(
        walletAddress: string,
        ruleId: string,
        updates: {
            name?: string;
            tokenAddress?: string;
            tokenSymbol?: string;
            tokenName?: string;
            tradeType?: 'buy' | 'sell';
            conditions?: Omit<AutoTraderCondition, 'id' | 'rule_id' | 'created_at'>[];
            solAmountPerTrade?: number;
            maxTradesToExecute?: number;
            slippagePercent?: number;
            tradingWalletAddresses?: string[];
            totalSolBudget?: number;
            configurationMetadata?: any;
        }
    ): Promise<boolean> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return false;
            }

            // Update the rule
            const ruleUpdates: any = {};
            if (updates.name !== undefined) ruleUpdates.name = updates.name;
            if (updates.tokenAddress !== undefined) ruleUpdates.token_address = updates.tokenAddress;
            if (updates.tokenSymbol !== undefined) ruleUpdates.token_symbol = updates.tokenSymbol;
            if (updates.tokenName !== undefined) ruleUpdates.token_name = updates.tokenName;
            if (updates.tradeType !== undefined) ruleUpdates.trade_type = updates.tradeType;
            if (updates.solAmountPerTrade !== undefined) ruleUpdates.sol_amount_per_trade = updates.solAmountPerTrade;
            if (updates.maxTradesToExecute !== undefined) ruleUpdates.max_trades_to_execute = updates.maxTradesToExecute;
            if (updates.slippagePercent !== undefined) ruleUpdates.slippage_percent = updates.slippagePercent;
            if (updates.tradingWalletAddresses !== undefined) ruleUpdates.trading_wallet_addresses = updates.tradingWalletAddresses;
            if (updates.totalSolBudget !== undefined) ruleUpdates.total_sol_budget = updates.totalSolBudget;
            if (updates.configurationMetadata !== undefined) ruleUpdates.configuration_metadata = updates.configurationMetadata;

            const { error: ruleError } = await adminSupabase
                .from('auto_trader_rules')
                .update(ruleUpdates)
                .eq('id', ruleId)
                .eq('user_id', user.id);

            if (ruleError) {
                console.error('Error updating auto trader rule:', ruleError);
                return false;
            }

            // Update conditions if provided
            if (updates.conditions !== undefined) {
                // Delete existing conditions
                await adminSupabase
                    .from('auto_trader_conditions')
                    .delete()
                    .eq('rule_id', ruleId);

                // Insert new conditions
                if (updates.conditions.length > 0) {
                    const conditionsToInsert = updates.conditions.map(condition => ({
                        rule_id: ruleId,
                        field: condition.field,
                        operator: condition.operator,
                        value: condition.value,
                        timeframe: condition.timeframe
                    }));

                    const { error: conditionsError } = await adminSupabase
                        .from('auto_trader_conditions')
                        .insert(conditionsToInsert);

                    if (conditionsError) {
                        console.error('Error updating auto trader conditions:', conditionsError);
                        return false;
                    }
                }
            }

            return true;
        } catch (error) {
            console.error('Error in updateAutoTraderRule:', error);
            return false;
        }
    },

    /**
     * Delete an auto trader rule
     */
    async deleteAutoTraderRule(walletAddress: string, ruleId: string): Promise<boolean> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return false;
            }

            // Check for existing trade history records
            const { data: existingTrades, error: checkError } = await adminSupabase
                .from('auto_trader_trade_history')
                .select('id')
                .eq('rule_id', ruleId)
                .eq('user_id', user.id);

            if (checkError) {
                console.error('Error checking existing trade history:', checkError);
                return false;
            }

            console.log(`📊 Found ${existingTrades?.length || 0} trade history records for auto trader rule ${ruleId}`);

            // Set rule_id to null to preserve trade history while allowing rule deletion
            if (existingTrades && existingTrades.length > 0) {
                console.log(`🔄 Setting rule_id to null for ${existingTrades.length} trade history records to preserve them...`);
                const { error: updateError } = await adminSupabase
                    .from('auto_trader_trade_history')
                    .update({ rule_id: null })
                    .eq('rule_id', ruleId)
                    .eq('user_id', user.id);

                if (updateError) {
                    console.error('Error updating trade history before rule deletion:', updateError);
                    return false;
                }
                console.log(`✅ Successfully preserved ${existingTrades.length} trade history records`);
            }

            // Now delete the rule (conditions will be deleted automatically due to CASCADE)
            console.log(`🗑️ Deleting auto trader rule ${ruleId}...`);
            const { error } = await adminSupabase
                .from('auto_trader_rules')
                .delete()
                .eq('id', ruleId)
                .eq('user_id', user.id);

            if (error) {
                console.error('Error deleting auto trader rule:', error);
                return false;
            }

            console.log(`✅ Successfully deleted auto trader rule ${ruleId}`);
            return true;
        } catch (error) {
            console.error('Error in deleteAutoTraderRule:', error);
            return false;
        }
    },

    /**
     * Toggle rule active status
     */
    async toggleRuleActive(walletAddress: string, ruleId: string): Promise<boolean> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return false;
            }

            // Get current status
            const { data: rule } = await adminSupabase
                .from('auto_trader_rules')
                .select('is_active')
                .eq('id', ruleId)
                .eq('user_id', user.id)
                .single();

            if (!rule) {
                console.error('Auto trader rule not found');
                return false;
            }

            // Toggle the status
            const { error } = await adminSupabase
                .from('auto_trader_rules')
                .update({ is_active: !rule.is_active })
                .eq('id', ruleId)
                .eq('user_id', user.id);

            if (error) {
                console.error('Error toggling auto trader rule status:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error in toggleRuleActive:', error);
            return false;
        }
    },

    /**
     * Record an auto trader trade execution
     */
    async recordAutoTraderTrade(
        walletAddress: string,
        ruleId: string,
        tokenAddress: string,
        tokenName: string,
        tokenSymbol: string,
        tradingWalletAddress: string,
        amountSol: number,
        slippagePercent: number,
        tradeType: 'buy' | 'sell',
        transactionSignature?: string,
        status: 'pending' | 'success' | 'failed' = 'pending',
        errorMessage?: string,
        triggeredConditions?: any[]
    ): Promise<AutoTraderTradeHistory | null> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return null;
            }

            const { data: trade, error } = await adminSupabase
                .from('auto_trader_trade_history')
                .insert({
                    rule_id: ruleId,
                    user_id: user.id,
                    token_address: tokenAddress,
                    token_name: tokenName,
                    token_symbol: tokenSymbol,
                    trading_wallet_address: tradingWalletAddress,
                    amount_sol: amountSol,
                    slippage_percent: slippagePercent,
                    trade_type: tradeType,
                    transaction_signature: transactionSignature,
                    status,
                    error_message: errorMessage,
                    triggered_conditions: triggeredConditions,
                    executed_at: status !== 'pending' ? new Date().toISOString() : undefined
                })
                .select()
                .single();

            if (error) {
                console.error('Error recording auto trader trade:', error);
                return null;
            }

            // Update rule statistics
            if (status === 'success') {
                // Get current values
                const { data: currentExecutedRule } = await adminSupabase
                    .from('auto_trader_rules')
                    .select('total_executed_trades')
                    .eq('id', ruleId)
                    .single();

                await adminSupabase
                    .from('auto_trader_rules')
                    .update({
                        total_executed_trades: (currentExecutedRule?.total_executed_trades || 0) + 1,
                        last_triggered_at: new Date().toISOString()
                    })
                    .eq('id', ruleId);
            }

            // Get current trigger count and increment
            const { data: currentTriggeredRule } = await adminSupabase
                .from('auto_trader_rules')
                .select('total_triggers')
                .eq('id', ruleId)
                .single();

            await adminSupabase
                .from('auto_trader_rules')
                .update({
                    total_triggers: (currentTriggeredRule?.total_triggers || 0) + 1
                })
                .eq('id', ruleId);

            return trade;
        } catch (error) {
            console.error('Error in recordAutoTraderTrade:', error);
            return null;
        }
    },

    /**
     * Get trade history for a user
     */
    async getUserTradeHistory(walletAddress: string, limit: number = 50): Promise<AutoTraderTradeHistory[]> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return [];
            }

            // Get trade history without requiring rule to exist (rule_id can be null)
            const { data: trades, error } = await adminSupabase
                .from('auto_trader_trade_history')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .limit(limit);

            if (error) {
                console.error('Error fetching auto trader trade history:', error);
                return [];
            }

            // Manually fetch rule names for trades that still have rule_id
            const tradesWithRuleNames = await Promise.all(
                (trades || []).map(async (trade) => {
                    if (trade.rule_id) {
                        // Try to get the rule name
                        const { data: rule } = await adminSupabase
                            .from('auto_trader_rules')
                            .select('name')
                            .eq('id', trade.rule_id)
                            .single();

                        return {
                            ...trade,
                            rule_name: rule?.name || trade.rule_name || 'Unknown Bot'
                        };
                    } else {
                        // Rule was deleted, use stored rule_name or show as "Deleted Bot"
                        return {
                            ...trade,
                            rule_name: trade.rule_name || 'Deleted Bot'
                        };
                    }
                })
            );

            return tradesWithRuleNames;
        } catch (error) {
            console.error('Error in getUserTradeHistory:', error);
            return [];
        }
    }
};

// Field display names for UI (same as sniper bot)
export const AUTO_TRADER_FIELD_LABELS = {
    marketCap: 'Market Cap (USD)',
    liquidity: 'Liquidity (USD)',
    holders: 'Holders Count',
    buys: 'Buy Count',
    sells: 'Sell Count',
    curvePercentage: 'Curve %',
    txAmount: 'Tokens Bought',
    txVolume: 'Transaction Volume (USD)',
    name: 'Token Name',
    symbol: 'Token Symbol'
}; 